import React, {useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ResetPassword as ResetPasswordRequest} from "../../service/AuthenticationService";
import FCAlertMsg, {TypeAlertMsg} from "../../components/FCObjets/FCAlertMsg/FCAlertMsg";
import {useToast} from "../../components/FCObjets/FCToast/FCToast";
import "../Login/Login.scss";

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const toast = useToast();

    const tokenMissing = !token;

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password.length < 8) {
            toast.error("Le mot de passe doit faire au moins 8 caractères.", "Validation");
            return;
        }
        if (password !== confirm) {
            toast.error("Les mots de passe ne correspondent pas.", "Validation");
            return;
        }

        setLoading(true);
        try {
            await ResetPasswordRequest(token, password);
            toast.success("Mot de passe mis à jour.", "Succès");
            navigate("/");
        } catch (err: any) {
            toast.error(err?.response?.data?.error || "Une erreur est survenue.", "Erreur");
            setLoading(false);
        }
    };

    return (
        <div className="loginPage">
            <div className="loginBg"/>

            <div className="loginCenter">
                <div className="loginMobileLogo">
                    <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="freeCompta" className="loginMobileIcon"/>
                    <h1 className="loginMobileTitle">Free Compta</h1>
                </div>

                <div className="loginCard">
                    <div className="loginCardHeader">
                        <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="freeCompta" className="loginCardLogoImg"/>
                        <div className="loginCardLogo">
                            <span className="loginCardLogoText">Free Compta</span>
                        </div>
                    </div>

                    <h2 className="loginCardTitle">Nouveau mot de passe</h2>
                    <p className="loginCardSubtitle">
                        Choisissez un nouveau mot de passe pour votre compte.
                    </p>

                    {tokenMissing && (
                        <FCAlertMsg
                            textMsg="Lien invalide. Demandez un nouveau lien depuis la page « Mot de passe oublié »."
                            type={TypeAlertMsg.ERROR}
                            activate={true}
                            title="Lien invalide"
                        />
                    )}

                    {!tokenMissing && (
                        <form onSubmit={onSubmit} className="loginForm">
                            <div className="loginFieldGroup">
                                <label className="loginFieldLabel">Nouveau mot de passe <span style={{fontSize: "11px", color: "var(--text-tertiary)", fontWeight: 400}}>(min. 8 caractères)</span></label>
                                <div className="loginFieldWrapper">
                                    <span className="material-symbols-outlined loginFieldIcon">lock</span>
                                    <input
                                        className="loginInput"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="new-password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="loginShowPassword"
                                        onClick={() => setShowPassword(v => !v)}
                                        tabIndex={-1}
                                    >
                                        <span className="material-symbols-outlined">
                                            {showPassword ? "visibility" : "visibility_off"}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="loginFieldGroup">
                                <label className="loginFieldLabel">Confirmer le mot de passe</label>
                                <div className="loginFieldWrapper">
                                    <span className="material-symbols-outlined loginFieldIcon">lock</span>
                                    <input
                                        className="loginInput"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        autoComplete="new-password"
                                        required
                                    />
                                </div>
                            </div>

                            <button type="submit" className="loginBtn" disabled={loading}>
                                {loading ? "Mise à jour…" : "Réinitialiser le mot de passe →"}
                            </button>
                        </form>
                    )}

                    {tokenMissing && (
                        <div className="loginForm">
                            <button type="button" className="loginBtn" onClick={() => navigate("/forgot-password")}>
                                Demander un nouveau lien
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <footer className="loginFooter">
                <a href="mailto:admin@free-compta.fr" className="loginFooterLink">Besoin d'aide ?</a>
                <span className="loginFooterDot">•</span>
                <span>Confidentialité</span>
                <span className="loginFooterDot">•</span>
                <span>© {new Date().getFullYear()} FREECOMPTA.</span>
            </footer>
        </div>
    );
}

export default ResetPassword;
